<template>
  <div class="plan-list" v-if="props.theme === 'card'">
    <div :class="`plan-item ${planList.length > 1 ? 'has-margin' : ''}`" v-for="item in planList" :key="item.planId" :data-plan-id="item.planId">
      <div :class="`plan ${item.planType} product-type-${item.saleProductType}-${item.isUnlimited} product-${item.saleProductType}-${item.planType}`">
        <button class="refresh" :class="{ refreshing: planRefreshing }" @click="handleRefreshPlan"></button>
        <div class="plan-wrapper">
          <p class="plan-row plan-title">{{ item.planTitle }}</p>
          <p class="plan-row plan-desc" v-html="item.planDesc"></p>
        </div>
        <div>
          <div class="plan-type">
            <p class="plan-row">
              {{ $t('Current Plan') }}: <span class="plan-type-name">{{ item.planTypeText }}</span>
            </p>
            <p class="plan-row" v-if="item.planTimeDesc" v-html="item.planTimeDesc"></p>
            <p class="plan-row accross-device" v-if="item.planType !== 'free' && !item.isCloud && item.saleProductType !== 'Sign'">
              <span class="label">{{ $t('Use Across Devices') }} : </span>
              <span v-for="(deviceType, index) in item.acrossDevices" :class="deviceType" :key="index"></span>
            </p>
          </div>
          <el-button v-if="item.actionBtnType" class="active-btn" size="small" @click="handleActionBtnClick(item)">
            {{ item.actionBtnText }}
          </el-button>
        </div>
      </div>
    </div>
  </div>
  <div class="plan-table overflow-x-auto" v-else v-loading="planRefreshing" ref="refTableParent">
    <table ref="refTable">
      <thead>
        <tr>
          <th>{{ $t('Product') }}</th>
          <th>{{ $t('Plan Types') }}</th>
          <th>{{ $t('Details') }}</th>
          <th class="align-center sticky">{{ $t('Actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in planList" :key="item.planId">
          <td class="font-bold">{{ item.planTitle }}</td>
          <td>{{ item.planTypeText }}</td>
          <td class="color-grey" v-html="item.planTimeDesc ? item.planTimeDesc : item.planDesc"></td>
          <td class="align-center sticky">
            <el-button v-if="item.actionBtnType" @click="handleActionBtnClick(item)" :type="item.actionBtnType.includes('upgrade') ? 'primary' : 'default'" size="small">
              {{ item.actionBtnText }}
            </el-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  
  <!--取消订阅确认弹窗-->
  <el-dialog v-model="cancelPlanDialog" :append-to-body="true" :show-close="false" class="no-header no-padding" style="width: max-content">
    <div class="cancel-plan-dialog">
      <span class="close-dialog-btn" @click="cancelPlanDialog = false"></span>
      <div class="left">
        <div>
          <p class="title">{{ $t('You will miss the following membership rights when the subscription expired.') }}</p>
          <p class="expired">{{ $t('Expire on {date}', { date: cancelPlanData.expireTime }) }}</p>
          <ul v-if="!showAICancelDialog && !showSignCancelDialog">
            <li v-for="(item, index) in cancelDialogList" :key="index">{{ $t(item) }}</li>
          </ul>
          <ul v-else-if="showSignCancelDialog">
            <li v-for="(item, index) in cancelDialogSignList" :key="index">{{ $t(item) }}</li>
          </ul>
          <ul v-else-if="hasUndefined">
            <li v-for="(item, index) in cancelDialogAiUnlimitedList" :key="index">{{ $t(item) }}</li>
          </ul>
          <ul v-else>
            <li v-for="(item, index) in cancelDialogAiStandardList" :key="index">{{ $t(item) }}</li>
          </ul>
        </div>
        <div class="btn-group">
          <el-button v-if="showSignCancelDialog" class="close-btn" type="primary" @click="cancelPlanDialog = false">{{ $t('Keep UPDF Sign') }}</el-button>
          <el-button v-if="!showSignCancelDialog" class="close-btn" type="primary" @click="cancelPlanDialog = false">{{
            $t(showAICancelDialog ? (hasUndefined ? 'Keep UPDF AI Unlimited' : 'Keep UPDF AI Standard') : 'Keep UPDF Pro')
          }}</el-button>
          <el-button class="cancel-btn" type="default" :loading="cancelBtnLoading" @click="handleCancelPlan()">{{ $t('Continue Cancellation') }}</el-button>
        </div>
      </div>
      <div class="right" :class="showAICancelDialog ? 'ai' : 'updf'"></div>
    </div>
  </el-dialog>
</template>

<script setup>
import { activeSubscription, cancelSubscription, getAuthCode, getPlanList, usePlan as usePlanApi } from '@/api/user/user'
import useTableSticked from '@/view/user/center/hooks/useTableSticked'
import emitter from '@/libs/emitter'
import enums from '@/libs/enums'
import gtagTools from '@/libs/gtag-tools'
import { formatTime } from '@/libs/tools'
import { getCheckoutUrl } from '@/services/base'
import { redirectToPricing } from '@/services/checkout'
import { appStore, userStore } from '@/stores'
import { ElMessage, ElMessageBox } from 'element-plus'
import { computed, getCurrentInstance, onMounted, onUnmounted, reactive, ref } from 'vue'

const { $t } = getCurrentInstance().proxy

const isCN = appStore.isCN

const props = defineProps({
  // 主题：card 卡片样式，table 表格样式
  theme: {
    type: String,
    default: 'card',
  },
  canAction: {
    // 是否操作，比如取消订阅，重新购买等按钮的显示
    type: Boolean,
    default: true,
  },
  planId: {
    // 指定planId(如果>0,则只显示该plan)
    type: Number,
    default: 0,
  },
})

const emits = defineEmits(['on-loaded'])
// plan刷新状态
const planRefreshing = ref(false)
// 取消订阅弹窗
const cancelPlanDialog = ref(false)
// 要取消的plan
const cancelPlanData = {
  planId: 0,
  expireTime: '',
  planType: '',
}
// 取消按钮loading
const cancelBtnLoading = ref(false)

// 取消弹窗左侧列表文案
const cancelDialogList = $t(
  'Use across Windows, macOS, Android, iOS++No watermark++All annotate tools++Edit PDF text, images and links++Convert PDF to Word, Excel, PPT++OCR Text Recognition++Extract, insert, replace, split pages++Free updates and upgrades'
).split('++')
// 取消AI标准版续费弹窗左侧列表文案
const cancelDialogAiStandardList = $t('Upload files up to 100 per month++Up to 1000 pages per file++Ask questions up to 1000 times per month++Up to 2GB per file++10GB of FREE ADDITIONAL STORAGE').split('++')
// 取消AI标准版续费弹窗左侧列表文案
const cancelDialogAiUnlimitedList = $t('Upload files unlimited per month++Up to 1000 pages per file++Ask questions unlimited times per month++Up to 2GB per file++100GB of FREE ADDITIONAL STORAGE').split('++')
const cancelDialogSignList = $t(
  'Legally binding digital signature (certified by AATL).++Automatically notify recipients when signature is sent.++Multiple file signing and batch sending.++Save and manage digital signatures for long-term use.++Provides audit trail and certification.'
).split('++')
// 激活订阅弹窗
const activeBtnLoading = ref(false)
// 过期时间
const getPlanExpiredTime = expireTime => {
  if (expireTime === 0) return ''
  if (expireTime === -1) return 'Indefinitely'
  return formatTime(expireTime * 1000, 'L')
}
// 是否为无限版AI
const hasUndefined = ref(false)

const showAICancelDialog = ref(false)
const showSignCancelDialog = ref(false)
// 云权益产品id
const cloudProductId = enums.goodsProductId.CLOUD
// UPDF 产品ID数组
const updfProductIds = enums.goodsProductId.UPDF
// AI产品i对象
const { AI_STANDARD_CN, AI_UNLIMITED_CN, AI_STANDARD_EN, AI_UNLIMITED_EN, SIGN_EN } = enums.goodsProductId
const aiProductIds = ref(isCN ? { unlimited: AI_UNLIMITED_CN, standard: AI_STANDARD_CN } : { unlimited: AI_UNLIMITED_EN, standard: AI_STANDARD_EN })
// 数字签名产品id
const signProductId = SIGN_EN
// 所有地区ai产品ID、
const allAiProductIds = [AI_STANDARD_CN, AI_UNLIMITED_CN, AI_STANDARD_EN, AI_UNLIMITED_EN]
/**
 * 支持的设备平台
 * @param {array} productIds 支持的设备类型ID数组
 * @param {bool}  all 是否是全平台，比如：free类型
 * @return {string} 返回支持的平台名称，多个用逗号隔开
 */
const getAcrossDevices = (productIds, all = false) => {
  const products = {
    25646: 'windows',
    25643: 'macOS',
    25644: 'iOS',
    25645: 'android',
  }
  // 这里不使用Object.values(products)是为了和后端顺序保持一致
  const allDevices = ['windows', 'macOS', 'iOS', 'android']
  if (all) return allDevices
  if (productIds) {
    const productNames = []
    for (let i = 0; i < productIds.length; i++) {
      if (products[productIds[i]]) {
        productNames.push(products[productIds[i]])
      }
    }
    return productNames
  }
  return []
}
// 订阅信息
const planList = reactive([])
// 是否含有终身plan
const hasLifetimePlan = computed(() => planList.filter(item => item.planType === 'lifetime').length > 0)

// 判断两个数组内容是否包含
const hasSameValues = (array1, array2) => {
  for (let i = 0; i < array1.length; i++) {
    if (array2.includes(array1[i])) {
      return true
    }
  }
  return false
}
// 更新plan信息
const handleRefreshPlan = () => {
  if (planRefreshing.value) return
  planRefreshing.value = true
  getPlanList()
    .then(({ data }) => {
      if (props.planId > 0) {
        data = data.filter(item => item.planId === props.planId)
      }
      planList.splice(0, planList.length)
      if (data.length > 0) {
        for (const plan of data) {
          const planData = {
            planTitle: '',
            planDesc: '',
            planTimeDesc: '',
            planTypeText: '',
            actionBtnType: '',
            actionBtnText: '',
            planId: plan.planId,
            orderId: plan.orderId,
            subscriptionId: plan.subscriptionId,
            canCancel: plan.canCancel, // 是否可取消订阅
            canRenewal: plan.canRenewal, // 是否可继续购买（针对微信等非订阅形式）
            canUse: plan.canUse, // 是否可激活（针对赠送的时长）
            canSubscription: plan.canSubscription, // 是否可继续订阅（针对已取消但未到期的订阅）
            hasUcloud: false, // 是否有云权益
            hasSign: false, // 是否有数字签名权益
            isAi: false, // 是否是Ai订阅
            isSign: false, // 是否为数字签名订阅
            isUPDF: false, // 是否是UPDF权益
            isUnlimited: false, // 是否为无限版AI
            expireMonth: plan.expireMonth,
            planType: plan.subscriptionType,
            saleProductType: 'UPDF',
            cancelTime: plan.cancelTime,
            name: plan.subscriptionName,
            createdAt: plan.createdAt,
            expireTimestamp: plan.expireTime,
            nextPayTimestamp: plan.nextPayTime,
            expireTime: getPlanExpiredTime(plan.expireTime),
            cloudExpireTime: getPlanExpiredTime(plan.expireTime),
            nextPayTime: getPlanExpiredTime(plan.nextPayTime),
            acrossDevices: getAcrossDevices(plan.productIds),
            isShowNextPayTime: plan.isShowNextPayTime, // 是否显示续订时间
            isShowExpireTime: plan.isShowExpireTime, // 是否显示到期时间
            productIds: plan.productIds, // 商品的Ids
            buttonLoading: false, // 操作按钮loading状态
          }
          // 免费试用商品类型
          if (planData.isFreeTrail) {
            planData.planType = 'freeTrail'
          }
          if (plan.productIds.includes(cloudProductId)) {
            planData.hasUcloud = true
            // 终身情况下云权益过期时间单独显示
            if (plan.cloudPlanExpire) planData.cloudExpireTime = getPlanExpiredTime(plan.cloudPlanExpire)
          }
          if (plan.productIds.includes(signProductId)) {
            planData.isSign = true
            planData.hasSign = true
            planData.saleProductType = 'Sign'
            planData.acrossDevices = getAcrossDevices([], true)
          }
          if (hasSameValues(plan.productIds, allAiProductIds)) {
            if (hasSameValues(plan.productIds, Object.values(aiProductIds.value))) {
              planData.isAi = true
              planData.saleProductType = 'AI'
              // 判断是否存在无限ai
              planData.isUnlimited = plan.productIds.includes(aiProductIds.value.unlimited)
              planData.acrossDevices = getAcrossDevices([], true)
            } else continue
          }
          if (hasSameValues(plan.productIds, updfProductIds)) {
            planData.isUPDF = true
          }
          // 单独的云权益需要去掉
          if (plan.productIds.length === 1 && plan.productIds.includes(cloudProductId)) {
            continue
          }
          // 国内排出掉sign产品
          if (isCN && planData.isSign) {
            continue
          }
          planList.push(planData)
        }
        // 云权益排最后
        planList.sort(a => (a.isCloud ? 1 : -1))
      }
      // 添加ai免费卡片
      if (!planList.some(item => item.isAi === true)) {
        planList.unshift({
          planId: 0,
          isAi: true,
          planType: 'freeAi',
          acrossDevices: getAcrossDevices([], true),
          expireTimestamp: 0,
          nextPayTimestamp: 0,
          canCancel: false,
          canRenewal: false,
          canUse: false,
          canSubscription: false,
          saleProductType: 'AI-free',
        })
      }
      // 添加updf免费卡片
      if (!planList.filter(item => item.isUPDF).length) {
        planList.unshift({
          planId: 1,
          isUPDF: true,
          planType: 'free',
          acrossDevices: getAcrossDevices([], true),
          expireTimestamp: 0,
          nextPayTimestamp: 0,
          canCancel: false,
          canRenewal: false,
          canUse: false,
          canSubscription: false,
          saleProductType: '',
        })
      }
      // 添加updf sign免费卡片
      if (!planList.filter(item => item.isSign).length && !appStore.isCN) {
        planList.unshift({
          planId: 2,
          isSign: true,
          planType: 'freeSign',
          acrossDevices: getAcrossDevices([], true),
          expireTimestamp: 0,
          nextPayTimestamp: 0,
          canCancel: false,
          canRenewal: false,
          canUse: false,
          canSubscription: false,
          saleProductType: 'Sign-free',
        })
      }
      for (const plan of planList) {
        // Plan标题
        plan.planTitle = $t('UPDF Plan')
        if (plan.saleProductType === 'AI-free') {
          plan.planTitle = $t('UPDF AI Plan')
        } else if (plan.saleProductType === 'AI') {
          plan.planTitle = $t(plan.isUnlimited ? 'UPDF AI Unlimited' : 'UPDF AI Standard')
        } else if (plan.saleProductType === 'Sign-free') {
          plan.planTitle = $t('UPDF Sign')
        } else if (plan.saleProductType === 'Sign') {
          plan.planTitle = $t(plan.planType === 'annually' ? 'UPDF Sign Annually' : 'UPDF Sign Monthly')
        }
        // Plan描述
        plan.planDesc = $t('Upgrade to UPDF Pro to get all UPDF features and UCloud benefits.')
        if (['lifetime'].includes(plan.planType) && plan.hasUcloud) {
          plan.planDesc = $t('Full access to all UPDF features and UCloud benefits.')
        } else if (['lifetime'].includes(plan.planType)) {
          plan.planDesc = $t('Full access to all UPDF features.')
        } else if (['quarterly', 'annually', 'monthly'].includes(plan.planType) && plan.saleProductType === 'UPDF') {
          plan.planDesc = $t('Full access to all UPDF features and UCloud benefits.')
        } else if (['quarterly', 'annually', 'monthly'].includes(plan.planType) && plan.saleProductType === 'Sign') {
          plan.planDesc = $t('Full access to all UPDF Sign features and UPDF Cloud benefits.')
        } else if (plan.saleProductType === 'AI') {
          plan.planDesc = $t('Full access to all UPDF AI features and UPDF Cloud benefits')
        } else if (plan.saleProductType === 'AI-free') {
          plan.planDesc = $t('Upgrade to UPDF AI Pro monthly Plan to unlock more AI features')
        } else if (plan.saleProductType === 'Sign-free') {
          plan.planDesc = $t('Upgrade to UPDF Sign Plan to unlock more Sign features')
        }
        // Plan类型名称
        plan.planTypeText = plan?.planType ? $t(enums?.planTypeName[plan?.planType]) : ''
        // 过期时间和续期时间的展示逻辑：
        // -免费、终身、赠品不展示，
        // -非订阅的：过期时间，
        // -只有订阅的：续期时间，
        // -订阅+兑换的：续期时间+过期时间
        plan.planTimeDesc = ''
        if (!['free', 'lifetime', 'giveaway'].includes(plan.planType)) {
          const planTimeDescArr = []
          if (plan.isShowNextPayTime) planTimeDescArr.push($t('Renews') + ':' + plan.nextPayTime)
          if (plan.isShowExpireTime) planTimeDescArr.push($t('Expire on') + ':' + plan.expireTime)
          plan.planTimeDesc = planTimeDescArr.join('<br>')
        } else if (plan.planType === 'giveaway') {
          // 赠品的展示逻辑：已激活=>过期时间N个月，未激活=>VIP时长N个月
          plan.planTimeDesc = $t(plan.canUse ? 'VIP Duration' : 'Expire on') + ':' + (plan.canUse ? plan.expireMonth + $t(plan.expireMonth === 1 ? 'Month' : 'Months') : plan.expireTime)
        }
        // 升级、订阅、取消订阅等按钮
        plan.actionBtnType = ''
        plan.actionBtnText = ''
        // mac appStore版本审核时需要隐藏
        if (props.canAction && !plan.isCloud && !appStore.isAppStore) {
          // 升级到专业版：如果是免费卡片或者是只有一个赠送的卡片且已激活
          if (['free', 'freeAi', 'freeSign'].includes(plan.planType) || (planList.length === 1 && plan.planType === 'giveaway' && !plan.canUse)) {
            plan.actionBtnType = {
              free: 'upgradeUpdf',
              freeAi: 'upgradeAi',
              freeSign: 'upgradeSign',
            }[plan.planType]
            plan.actionBtnText = $t('Upgrade')
          }
          // 赠送的且未使用的显示激活按钮
          else if (plan.planType === 'giveaway' && plan.canUse) {
            plan.actionBtnType = 'activeGiveaway'
            plan.actionBtnText = $t('Activate')
          }
          // 取消订阅：canCancel=true 说明是正常订阅的
          else if (plan.canCancel) {
            plan.actionBtnType = 'cancelSubscription'
            plan.actionBtnText = $t('Unsubscribe')
          }
          // 恢复订阅：针对已取消订阅的plan，注意由免费到兑换码兑换的plan canSubscription也为true，所以该按钮判断要结合canRenewal为false时才显示
          else if (plan.canSubscription && !plan.canRenewal) {
            plan.actionBtnType = 'activeSubscription'
            plan.actionBtnText = $t('Subscribe')
          }
          // 续订：其他单次购买或兑换码兑换的情况
          else if (plan.canRenewal) {
            plan.actionBtnType = 'renew'
            plan.actionBtnText = $t('Renew')
          }
        }
      }
      // 按照UPDF、AI、UPDF Sign， Cloud的顺序进行排序
      planList.sort((a, b) => {
        function getIndex (item) {
          if (item.isUPDF) return 1
          if (item.isAi) return 2
          if (item.isSign) return 3
          return 4
        }
        console.log(getIndex(a), getIndex(b))
        return getIndex(a) - getIndex(b)
      })
      userStore.planList.splice(0, userStore.planList.length, ...planList)
      // 如果有指定planId。则只筛选此planId的卡片
      if (props.planId) {
        const customPlanList = planList.filter(item => item.planId === props.planId)
        console.log(customPlanList)
        planList.splice(0, planList.length)
        planList.push(...customPlanList)
      }
      emits('on-loaded', planList)
    })
    .catch(err => {
      console.log(err)
    })
    .finally(() => {
      setTimeout(() => {
        planRefreshing.value = false
      }, 1000)
    })
}

// 卡片上操作按钮点击事件
const handleActionBtnClick = plan => {
  if (plan.actionBtnType === 'upgradeUpdf') {
    // UPDF 升级
    handleUpgradePro()
  } else if (['upgradeAi', 'upgradeSign'].includes(plan.actionBtnType)) {
    // AI 或 Sign 卡片升级
    handleRenewalPlan(plan.saleProductType)
  } else if (plan.actionBtnType === 'activeGiveaway') {
    // 激活
    handleUsePlan(plan)
  } else if (plan.actionBtnType === 'cancelSubscription') {
    // 取消订阅
    handleShowCancelPlanDialog(plan)
  } else if (plan.actionBtnType === 'activeSubscription') {
    // 恢复订阅
    handleActiveSubscription(plan)
  } else if (plan.actionBtnType === 'renew') {
    // 续订：其他单次购买或兑换码兑换的情况
    handleRenewalPlan(plan.saleProductType)
  }
}

// 弹出取消plan弹窗
const handleShowCancelPlanDialog = plan => {
  hasUndefined.value = plan.productIds.includes(aiProductIds.value.unlimited)
  showAICancelDialog.value = plan.isAi
  showSignCancelDialog.value = plan.isSign
  Object.assign(cancelPlanData, plan)
  cancelPlanDialog.value = true
  const purchaseDays = parseInt((new Date().getTime() / 1000 - plan.createdAt) / 86400)
  gtagTools.cancelPage(plan.planType, purchaseDays)
}
// 取消plan
const handleCancelPlan = () => {
  if (!props.canAction || cancelPlanData.planId === 0 || cancelBtnLoading.value) return
  cancelBtnLoading.value = true
  const purchaseDays = parseInt((new Date().getTime() / 1000 - cancelPlanData.createdAt) / 86400)
  gtagTools.cancelSubscription(cancelPlanData.planType, purchaseDays)
  cancelSubscription(cancelPlanData.planId)
    .then(() => {
      ElMessage.success($t('Cancel success'))
    })
    .finally(() => {
      cancelBtnLoading.value = false
      cancelPlanDialog.value = false
      activeBtnLoading.value = false
      handleRefreshPlan()
    })
}
// 重新激活订阅
const handleActiveSubscription = plan => {
  if (plan.buttonLoading) return
  plan.buttonLoading = true
  const purchaseDays = parseInt((new Date().getTime() / 1000 - plan.cancelTime) / 86400)
  gtagTools.restoreSubscription(plan.planType, purchaseDays)
  activeSubscription(plan.planId)
    .then(() => {
      if (plan.isAi) {
        ElMessageBox.alert(
          $t('Success! Your {aiType} subscription will be renewed on {date}', { date: plan.expireTime, aiType: plan.productIds.includes(aiProductIds.value.unlimited) ? $t('UPDF AI Unlimited') : $t('UPDF AI Standard') }),
          $t('Activate Subscription'),
          {
            center: true,
          }
        )
      } else if (plan.isSign) {
        ElMessageBox.alert(
          $t('Success! Your {aiType} subscription will be renewed on {date}', { date: plan.expireTime, aiType: $t('UPDF Sign') }),
          $t('Activate Subscription'),
          {
            center: true,
          }
        )
      } else {
        ElMessageBox.alert(
          $t('Success! Your UPDF PRO subscription will be renewed on {date}', { date: plan.expireTime }), 
          $t('Activate Subscription'),
          {
            center: true,
          }
        )
      }
    })
    .catch(err => {
      console.log('activeSubscriptErr', err)
    })
    .finally(() => {
      plan.buttonLoading = false
      handleRefreshPlan()
    })
}

// 激活赠品弹窗
const handleUsePlan = plan => {
  if (plan.buttonLoading) return
  // 如果还有其它订阅或plan，则弹出确认框
  if (planList.length >= 2) {
    ElMessageBox.confirm(
      $t(
        'As you already have a subscription plan, activating the "Giveaway" plan would make both plans run on your account, but it will not increase the original subscription time. Are you sure you want to activate the "Giveaway" plan?'
      ),
      $t('Activate the "Giveaway" plan'),
      {
        confirmButtonText: $t('Yes'),
        cancelButtonText: $t('No'),
        beforeClose(action, instance, done) {
          if (action === 'confirm') {
            instance.confirmButtonLoading = true
            usePlan(plan)
              .then(() => {
                done()
              })
              .finally(() => {
                instance.confirmButtonLoading = false
              })
          } else {
            done()
          }
        },
      }
    )
  } else {
    usePlan(plan)
  }
}

// 激活赠品
const usePlan = async plan => {
  const planId = plan.planId
  const expireMonth = plan.expireMonth
  plan.buttonLoading = true
  try {
    await usePlanApi(planId)
    handleRefreshPlan()
    let otherPlanType = 'free'
    if (planList.length > 1) {
      const otherPlan = planList.filter(item => item.planId !== planId)
      if (otherPlan.length) otherPlanType = otherPlan.planType
    }
    const registerDays = parseInt((new Date().getTime() / 1000 - userStore.userInfo.registerAt) / 86400)
    gtagTools.activeGiveaway(expireMonth, otherPlanType, registerDays)
  } catch (err) {
    handleRefreshPlan()
    console.log('usePlanErr', err)
  }
  plan.buttonLoading = false
}
/**
 * 跳转到收银台
 * @param {Object} urlQuery 进入收银台时自定义参数
 */
const gotoCheckout = async (urlQuery, pricingListType) => {
  if (isCN) {
    let checkoutUrl = getCheckoutUrl(urlQuery)
    if (window === window.parent) {
      const { data } = await getAuthCode()
      if (urlQuery) {
        checkoutUrl += `&${urlQuery}`
      }
      checkoutUrl += `&code=${data.code}&userSync=1`
    } else {
      if (urlQuery) checkoutUrl += `&${urlQuery}`
    }
    window.open(checkoutUrl, '_blank')
  } else {
    redirectToPricing(pricingListType)
  }
}

// 升级为Pro
const handleUpgradePro = () => {
  gtagTools.upgradePro()
  gotoCheckout()
}

// 立即续费
const handleRenewalPlan = saleProductType => {
  gtagTools.renew()
  if (saleProductType === 'AI' || saleProductType === 'AI-free') gotoCheckout({ saleProductType: 'AI', subscriptionType: 'monthly' })
  else if (saleProductType === 'Sign' || saleProductType === 'Sign-free') gotoCheckout('', 3)
  else gotoCheckout()
}

const refTable = ref()
const refTableParent = ref()
useTableSticked(refTableParent, refTable)

onMounted(() => {
  handleRefreshPlan()
  emitter.on('refreshPlanList', handleRefreshPlan)
})

onUnmounted(() => {
  emitter.off('refreshPlanList', handleRefreshPlan)
})
</script>

<style lang="scss" scoped>
table {
  min-width: 600px;
}

.cancel-plan-dialog {
  width: 800px;
  display: flex;
  position: relative;
  padding: 20px;
  box-sizing: border-box;
  .close-dialog-btn {
    display: block;
    width: 32px;
    height: 32px;
    position: absolute;
    background: url(@/assets/icon-close.png) center no-repeat;
    background-size: contain;
    right: 25px;
    top: 25px;
    cursor: pointer;
  }
  .left {
    width: 50%;
    word-break: break-word;
    padding: 30px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .title {
      font-weight: bold;
      font-size: 17px;
      line-height: 24px;
      color: #000;
    }
    .expired {
      margin-top: 5px;
      color: #666;
    }
    ul {
      margin-top: 26px;
      li {
        width: 100%;
        list-style-type: none;
        padding-left: 20px;
        font-size: 14px;
        line-height: 16px;
        color: #000;
        margin: 4px 0;
        background: url(@/assets/icon-no.png) left center no-repeat;
        background-size: 16px;
        display: inline-block;
      }
    }
    .btn-group {
      margin-top: 30px;
      .close-btn,
      .cancel-btn {
        width: 100%;
        height: 43px;
        margin-left: 0;
        margin-bottom: 15px;
        border-radius: 8px;
      }
      .cancel-btn {
        border-color: #353535;
        margin-bottom: 0;
        &:hover {
          color: inherit;
        }
      }
    }
  }
  .right {
    width: 50%;
    border-radius: 16px;
    background-image: url(@/assets/cancel-plan-bg.png);
    background-size: cover;
    background-position: center;
    &.ai {
      background-image: url(@/assets/cancel-ai-plan-bg.png);
    }
  }
}
.redeem-plan-dialog {
  .el-dialog__body {
    .redeem-input {
      margin-bottom: 10px;
    }
    .result-msg {
      color: #3db5f9;
      &.is-error {
        color: var(--el-color-error);
      }
    }
  }
}
.ar-SA {
  .dialog-body {
    .left {
      .cancel-btn {
        margin-right: 0 !important;
      }
    }
  }
}
@media screen and (max-width: 800px) {
  .cancel-plan-dialog {
    width: 90vw;
    max-width: 400px;
    .left {
      width: 100%;
      .btn-group {
        margin-top: 50px;
      }
    }
    .right {
      display: none;
    }
  }
}
</style>
<style lang="scss" scoped>
.plan-table {
  border-radius: 12px;
}
.plan-list {
  display: inline-flex;
  flex-wrap: wrap;
  width: 100%;
  .plan-item {
    margin-right: 15px;
    &.has-margin {
      margin-bottom: 10px;
      margin-right: 10px;
    }
    &:last-child {
      margin-right: 0;
    }
    .plan {
      width: 330px;
      height: 100%;
      min-height: 222px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 16px;
      box-sizing: border-box;
      border-radius: 16px;
      overflow: hidden;
      background-image: url(@/assets/subscribe-card-free.png);
      background-size: cover;
      position: relative;
      color: #fff;
      &.freeAi {
        background-image: url(@/assets/subscribe-card-ai.png);
      }
      &.annually {
        background-image: url(@/assets/subscribe-card-annual.png);
      }
      &.monthly {
        background-image: url(@/assets/subscribe-card-monthly.png);
      }
      &.lifetime {
        background-image: url(@/assets/subscribe-card-lifetime.png);
      }
      &.quarterly {
        background-image: url(@/assets/subscribe-card-quarterly.png);
      }
      &.ucloud {
        background-image: url(@/assets/subscribe-card-cloud.png);
      }
      &.ai {
        background-image: url(@/assets/subscribe-card-ai.png);
      }
      &.product-type-AI-false {
        background-image: url(@/assets/subscribe-card-ai-standard-plan.png);
      }
      &.product-type-AI-true {
        background-image: url(@/assets/subscribe-card-ai-unlimited-plan.png);
      }
      &.product-Sign-monthly,
      &.product-Sign-annually {
        background-image: url(@/assets/subscribe-card-esign-monthly-plan.png);
      }
      .refresh {
        width: 24px;
        height: 24px;
        background: url(@/assets/subscribe-refresh.png) center no-repeat;
        background-size: cover;
        position: absolute;
        right: 12px;
        top: 12px;
        border: none;
        cursor: pointer;
        transition: all 0.5s;
        &.refreshing {
          animation: 1.5s linear 0s normal none infinite animation-rotate;
        }
      }
      .plan-row {
        font-size: 12px;
        padding: 2px 0;
        &.accross-device {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 2px;
          .label {
            align-self: flex-start;
            width: auto;
            line-height: 24px;
            word-wrap: break-word;
          }
          span {
            display: inline-block;
            width: 24px;
            height: 24px;
            background-size: cover;
            margin: 0 2px;
            flex: none;
            &.macOS {
              background-image: url(@/assets/plan-devicetype-macOS.png);
            }
            &.android {
              background-image: url(@/assets/plan-devicetype-android.png);
            }
            &.iOS {
              background-image: url(@/assets/plan-devicetype-iOS.png);
            }
            &.windows {
              background-image: url(@/assets/plan-devicetype-windows.png);
            }
          }
        }
        &.plan-title {
          font-size: 16px;
          font-weight: bold;
          > span {
            color: #8654ff;
          }
        }
      }
      .plan-title {
        font-size: 16px;
        font-weight: bold;
        :deep(> span) {
          color: #8654ff;
        }
      }
      .plan-desc {
        margin-top: 8px;
        font-size: 12px;
        color: #fff;
        opacity: 0.7;
      }
      .plan-type-name {
        font-size: 24px;
        font-weight: bold;
      }
      .plan-type {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        text-align: right;
      }
      .active-btn {
        border: none;
        width: 100%;
        height: 32px;
        max-width: 280px;
        display: block;
        margin: 0 auto;
        margin-top: 16px;
        border-radius: 4px;
      }
    }
  }
}
.redeem-btn {
  color: #36a8e8;
  margin-top: 15px;
  &:hover {
    &::after {
      content: '';
      width: 120%;
      background: rgba(54, 168, 232, 0.1);
      height: 26px;
      position: absolute;
      border-radius: 4px;
    }
  }
}

.ar-SA {
  .plan-list .plan-item .plan .refresh {
    left: 12px;
    right: unset;
  }
}
.fr-FR {
  .plan-list .plan-item .plan {
    width: 360px;
  }
}
@media screen and (max-width: 640px) {
  .plan-list {
    .plan-item {
      width: 100%;
      &.has-margin {
        margin-right: 0;
      }
      .plan {
        width: 100%;
        margin-right: 0;
      }
    }
  }
  .fr-FR {
    .plan-list .plan-item .plan {
      transform: scale(0.8);
      position: relative;
      right: 20px;
    }
  }
  .plan-list .plan-item .plan .plan-desc {
    font-size: 12px;
  }
  .plan-list .plan-item .plan .plan-row.accross-device .label {
    flex: 1;
    height: auto;
    line-height: normal;
    font-size: 12px;
  }
}
</style>
